import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
// import ThemeToggle from '../ThemeToggle'; // Commented out - dark mode is default

const NavBar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    // Close menu when route changes
    useEffect(() => {
        setIsOpen(false);
    }, [location]);

    // Prevent body scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleClick = () => {
        setIsOpen(!isOpen);
    };

    const navItems = [
        { label: 'À propos', to: '/#about', hash: true },
        { label: 'Services', to: '/services', hash: false },
        { label: 'Portfolio', to: '/portfolio', hash: false },
        { label: 'Contactez-nous', to: '/contact#contact', hash: true },
        { label: 'Démo de nos services', to: '/get-demo#demo', hash: true, cta: true }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        },
        exit: {
            opacity: 0,
            transition: {
                staggerChildren: 0.05,
                staggerDirection: -1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        },
        exit: {
            opacity: 0,
            y: 50,
            scale: 0.9,
            transition: {
                duration: 0.3
            }
        }
    };

    return (
        <>
            {/* Hamburger Menu Button */}
            <motion.button
                className="fixed top-6 right-6 z-50 p-3 rounded-lg bg-white dark:bg-black hover:bg-gray-50 dark:hover:bg-[#111111] transition-colors"
                onClick={handleClick}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <div className="w-6 h-5 flex flex-col justify-between">
                    <motion.span 
                        className="block w-full h-0.5 bg-gray-900 dark:bg-white origin-center"
                        animate={{
                            rotate: isOpen ? 45 : 0,
                            y: isOpen ? 8 : 0
                        }}
                        transition={{ duration: 0.3 }}
                    />
                    <motion.span 
                        className="block w-full h-0.5 bg-gray-900 dark:bg-white"
                        animate={{
                            opacity: isOpen ? 0 : 1,
                            scale: isOpen ? 0 : 1
                        }}
                        transition={{ duration: 0.3 }}
                    />
                    <motion.span 
                        className="block w-full h-0.5 bg-gray-900 dark:bg-white origin-center"
                        animate={{
                            rotate: isOpen ? -45 : 0,
                            y: isOpen ? -8 : 0
                        }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </motion.button>

            {/* Fullscreen Navigation Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 z-40 bg-white dark:bg-black"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        {/* Background Pattern/Grid Effect */}
                        <motion.div
                            className="absolute inset-0 opacity-5 dark:opacity-10"
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <div className="w-full h-full" style={{
                                backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
                                backgroundSize: '50px 50px'
                            }} />
                        </motion.div>

                        {/* Navigation Content */}
                        <motion.div
                            className="relative z-10 h-full flex flex-col items-center justify-center"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            {/* Logo */}
                            <motion.div
                                variants={itemVariants}
                                className="mb-16"
                            >
                                <HashLink smooth to="/#hero" onClick={() => setIsOpen(false)}>
                                    <motion.img 
                                src="/logo_tomorrow.png" 
                                alt="Tomorrow Global" 
                                        className="h-16 md:h-20 w-auto dark:brightness-0 dark:invert"
                                        whileHover={{ scale: 1.1, rotate: [0, -5, 5, -5, 0] }}
                                        transition={{ duration: 0.5 }}
                            />
                    </HashLink>
                            </motion.div>

                            {/* Navigation Links */}
                            <nav className="flex flex-col items-center space-y-6 md:space-y-8">
                                {navItems.map((item, index) => (
                                    <motion.div
                                        key={item.to}
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.05, x: 10 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        {item.hash ? (
                                            <HashLink
                                                smooth
                                                to={item.to}
                                                onClick={() => setIsOpen(false)}
                                                className={`block text-3xl md:text-5xl lg:text-6xl font-black transition-colors ${
                                                    item.cta 
                                                        ? 'text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 px-8 py-4 rounded-xl' 
                                                        : 'text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300'
                                                }`}
                                            >
                                                {item.label}
                                            </HashLink>
                                        ) : (
                                            <HashLink
                                                to={item.to}
                                                onClick={() => setIsOpen(false)}
                                                className={`block text-3xl md:text-5xl lg:text-6xl font-black transition-colors ${
                                                    item.cta 
                                                        ? 'text-white bg-gray-900 dark:bg-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 px-8 py-4 rounded-xl' 
                                                        : 'text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300'
                                                }`}
                                            >
                                                {item.label}
                                            </HashLink>
                                        )}
                                    </motion.div>
                                ))}
                            </nav>

                            {/* Theme Toggle - Commented out, dark mode is default */}
                            {/* <motion.div
                                variants={itemVariants}
                                className="mt-16"
                            >
                                <ThemeToggle />
                            </motion.div> */}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default NavBar;
